"use client";
import {
  analyticsAllowed,
  CONSENT_EVENT,
  synchronizeAnalyticsConsent,
} from "./consent";
import { CHECKPOINT_MS } from "./engagement-clock";
import { ViewCoordinator } from "./view-coordinator";

let coordinator: ViewCoordinator | null = null;
async function send(path: string, data: unknown) {
  const response = await fetch(path, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
    keepalive: true,
    signal: AbortSignal.timeout(5000),
    cache: "no-store",
  });
  if (!response.ok) throw new Error("Analytics delivery unavailable");
  return response;
}
export function browserTracking() {
  synchronizeAnalyticsConsent();
  if (coordinator) return coordinator;
  const instance = new ViewCoordinator({
    now: () => performance.now(),
    allowed: analyticsAllowed,
    foreground: () =>
      document.visibilityState === "visible" && document.hasFocus(),
    visit: async (data) => {
      const response = await send("/api/analytics/visit", data);
      const body = await response.json();
      if (!body.accepted) throw new Error("Analytics collection declined");
      return body.accepted && typeof body.receipt === "string"
        ? body.receipt
        : null;
    },
    engagement: async (eventId, receipt, activeMs) => {
      await send("/api/analytics/engagement", { eventId, receipt, activeMs });
    },
  });
  coordinator = instance;
  const changed = () => instance.flush();
  const activity = () => instance.activity();
  document.addEventListener("visibilitychange", changed);
  window.addEventListener("focus", changed);
  window.addEventListener("blur", changed);
  window.addEventListener(CONSENT_EVENT, changed);
  window.addEventListener("storage", changed);
  window.addEventListener("pagehide", () => instance.suspend());
  for (const type of [
    "pointerdown",
    "pointermove",
    "keydown",
    "scroll",
    "touchstart",
  ] as const)
    window.addEventListener(type, activity, { passive: true, capture: true });
  window.setInterval(() => instance.flush(), CHECKPOINT_MS);
  return instance;
}
